import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { CountyMap, Opportunity, QueryResult } from "../types.js";
import { sectionHead, oppItem } from "../components.js";
import { money, moneyShort, pct, signedPct, number } from "../format.js";
import { hbars } from "../charts.js";
import type { MapMetric } from "../map.js";
import "../map.js";

interface Data { map: CountyMap; opps: Opportunity[] }

let metric: MapMetric = "revenue";
let showLow = true;
let selected: string | null = null;
let detail: { fips: string; customers: QueryResult | null; error: string | null } | null = null;

function select(app: App, fips: string | null) {
  selected = fips;
  if (!fips) { detail = null; app.render(); return; }
  detail = { fips, customers: null, error: null };
  app.render();
  const { from, to } = app.yearRange();
  api.query({ dims: ["customer"], metrics: ["revenue"], filters: { ...app.queryFilters(), county: [fips] }, range: "custom", from, to, compare: "prior_year", limit: 12 })
    .then((r) => { if (detail?.fips === fips) { detail.customers = r; app.render(); } })
    .catch((e: Error) => { if (detail?.fips === fips) { detail.error = e.message; app.render(); } });
}

export const mapview: View<Data> = {
  title: () => "Coverage Map",
  filters: ["year", "manager", "rep", "division"],
  async load(app, param) {
    const [map, opps] = await Promise.all([api.map(app.q()), api.opportunities(app.q())]);
    if (param && /^\d{5}$/.test(param) && param !== selected) queueMicrotask(() => select(app, param));
    return { map, opps: opps.items };
  },
  render(app: App, d) {
    const counties = d.map.counties;
    const withSales = counties.filter((c) => c.ytd > 0);
    const low = counties.filter((c) => c.lowConversion);
    const repSel = app.filters.rep;
    const repTerr = repSel !== null ? counties.filter((c) => c.reps.includes(repSel)) : null;
    const sel = selected ? counties.find((c) => c.fips === selected) ?? null : null;
    const countyOpps = selected ? d.opps.filter((o) => o.county === selected) : [];
    const setMetric = (m: MapMetric) => { metric = m; app.render(); };
    const total = withSales.reduce((s, c) => s + c.ytd, 0);
    return html`
      <div class="row" style="margin-bottom:14px;flex-wrap:wrap;gap:12px">
        <div class="seg" role="group" aria-label="Color counties by">
          ${([["revenue", "Revenue"], ["yoy", "Change vs last year"], ["conversion", "Quote win rate"], ["coverage", repSel !== null ? "Territory" : "Rep coverage"]] as const)
            .map(([k, l]) => html`<button aria-pressed=${metric === k} @click=${() => setMetric(k)}>${l}</button>`)}
        </div>
        <label class="check"><input type="checkbox" .checked=${showLow} @change=${(e: Event) => { showLow = (e.target as HTMLInputElement).checked; app.render(); }}> Flag low-conversion counties</label>
        <span class="spacer"></span>
        <span class="muted small">${repSel !== null ? `Territory outlined: ${app.me.reps.find((r) => r.id === repSel)?.name}` : "Pick a rep above to outline their territory"}</span>
      </div>
      <div class="grid g-2" style="align-items:start">
        <section class="glass card" style="padding:12px">
          <ff-map .data=${{
            counties, metric, showLowConversion: showLow, selected, height: window.innerWidth < 760 ? 440 : 620,
            territoryRepIds: repSel !== null ? [repSel] : null,
            fitKey: `${app.filters.manager}-${repSel}-${app.filters.division}-${d.map.year}`,
            onSelect: (f: string | null) => select(app, f),
          }}></ff-map>
        </section>
        <section class="glass card">
          ${sel ? html`
            ${sectionHead(sel.label, `Top rep: ${sel.topRepName}`, html`<button class="btn ghost" @click=${() => select(app, null)}>Close</button>`)}
            <div class="panel-list">
              <div class="kv"><span>Revenue YTD</span><b>${money(sel.ytd)}</b></div>
              <div class="kv"><span>Same period last year</span><b>${money(sel.priorYtd)}</b></div>
              <div class="kv"><span>Change</span><b>${signedPct(sel.yoy)}</b></div>
              <div class="kv"><span>Customers buying this year</span><b>${number(sel.customers)}</b></div>
              <div class="kv"><span>Quotes won, last 12 months</span><b>${sel.quotesResolved ? `${pct(sel.conversion)} (${sel.quotesWon} of ${sel.quotesResolved})` : "No decided quotes"}</b></div>
              <div class="kv"><span>Reps active (2 yrs)</span><b style="text-align:right;white-space:normal">${sel.reps.map((r) => app.me.reps.find((x) => x.id === r)?.name ?? `#${r}`).join(", ") || "—"}</b></div>
            </div>
            ${sel.lowConversion ? html`<div class="banner err mt" style="margin-bottom:0">Win rate here is well below the ${pct(d.map.overallConversion)} average. Worth a conversation.</div>` : nothing}
            <div class="mt">${sectionHead("Customers here", "This year · tick = same period last year")}</div>
            ${detail?.error ? html`<div class="err-text">${detail.error}</div>`
              : !detail?.customers ? html`<div class="skeleton" style="height:120px"></div>`
              : hbars(detail.customers.rows.map((r) => ({ label: r.label.customer ?? "", value: r.values.revenue ?? 0, ghost: r.prior?.revenue ?? null })), moneyShort, { ghostName: "Last year", empty: "No sales here this year." })}
            ${countyOpps.length ? html`<div class="mt">${sectionHead("Opportunities here")}</div>${countyOpps.slice(0, 8).map((o) => oppItem(o))}` : nothing}
          ` : html`
            ${sectionHead("Coverage", repTerr ? `${repTerr.length} counties in territory` : `${withSales.length} counties buying this year`)}
            <div class="panel-list">
              <div class="kv"><span>Revenue mapped to a county</span><b>${money(total)}</b></div>
              <div class="kv"><span>Counties with sales this year</span><b>${number(withSales.length)}</b></div>
              <div class="kv"><span>Overall quote win rate (12 mo)</span><b>${pct(d.map.overallConversion)}</b></div>
              <div class="kv"><span>Low-conversion counties</span><b>${number(low.length)}</b></div>
            </div>
            <div class="mt">${sectionHead("Top counties", "Click one for detail")}</div>
            ${hbars([...withSales].sort((a, b) => b.ytd - a.ytd).slice(0, 10).map((c) => ({ label: c.label, value: c.ytd, ghost: c.priorYtd, onClick: () => select(app, c.fips) })), moneyShort, { ghostName: "Last year" })}
            ${low.length ? html`<div class="mt">${sectionHead("Low conversion", `Under ${pct(d.map.lowConversionThreshold)} with 5+ decided quotes`)}</div>
              ${hbars(low.sort((a, b) => b.quotesResolved - a.quotesResolved).slice(0, 8).map((c) => ({ label: c.label, value: c.conversion ?? 0, onClick: () => select(app, c.fips), title: `${c.quotesWon} of ${c.quotesResolved} won` })), (n) => pct(n))}` : nothing}
          `}
        </section>
      </div>`;
  },
};
